// lazyWithRetry.ts — makes React.lazy route chunks resilient to stale-deploy 404s.
// After a new deploy the old hash-named chunk (e.g. Batches-CRIzmNgG.js) no longer exists on the
// server, so a dynamic import() rejects with "Failed to fetch dynamically imported module" and the
// app crashes. Instead, we reload once (guarded against loops) to pull the fresh index.html +
// current chunk names, which transparently recovers the navigation.
import { lazy, type ComponentType } from 'react';

const RELOAD_KEY = 'stale-chunk-reload-at';
const RELOAD_WINDOW_MS = 12_000;

/** Reload once to recover from a stale chunk. Returns false if we already reloaded very recently
 *  (so a genuinely-missing chunk surfaces its error instead of looping the page forever). */
export function reloadForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode / blocked) — still attempt the reload
  }
  window.location.reload();
  return true;
}

/** React.lazy that recovers from stale-chunk import failures by reloading to the fresh build. */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (reloadForStaleChunk()) {
        // The document is about to be replaced by the reload — never resolve so nothing
        // renders (and no error boundary trips) in the meantime.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
}
